
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { isBrowser } from "@/utils/runtime";
import { leadFormSchema } from "@/utils/validation";
import { sanitizeFormData } from "@/utils/sanitizer";
import { createRateLimiter } from "@/utils/validation";

// Rate limiter: max 3 submissions per 10 minutes per IP
const rateLimiter = createRateLimiter(3, 10 * 60 * 1000);

const TocFotosContactForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Honeypot field to catch bots
  const [honeypot, setHoneypot] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Check honeypot field (should be empty)
    if (honeypot) {
      return; // Silent fail for bots
    }
    
    // Rate limiting check
    const userIdentifier = isBrowser ? globalThis.location.hostname : 'unknown';
    if (!rateLimiter.isAllowed(userIdentifier)) {
      const remainingTime = Math.ceil(rateLimiter.getRemainingTime(userIdentifier) / 1000 / 60);
      toast.error(`Muitas tentativas. Tente novamente em ${remainingTime} minutos.`);
      return;
    }
    
    setLoading(true);
    
    try {
      // Sanitize and validate form data
      const formData = sanitizeFormData({ name, email, phone });
      
      const validation = leadFormSchema.safeParse(formData);
      if (!validation.success) {
        const fieldErrors: Record<string, string> = {};
        validation.error.errors.forEach((error) => {
          if (error.path[0]) {
            fieldErrors[error.path[0] as string] = error.message;
          }
        });
        setErrors(fieldErrors);
        toast.error('Por favor, corrija os erros no formulário.');
        return;
      }
      
      // Get the current page path to store as origin
      const currentPage = isBrowser ? globalThis.location.pathname : '';
      
      // Insert lead into the database with validated data
      const payload = {
        name: validation.data.name,
        email: validation.data.email,
        phone: validation.data.phone,
        page: currentPage,
        honeypot: "",
      };

      const { error } = await supabase
        .from("leads")
        .insert(payload);
      
      if (error) {
        throw error;
      }
      
      toast.success('Obrigado pelo seu interesse! Redirecionando para o painel...');
      
      // Reset form
      setEmail('');
      setPhone('');
      setName('');
      setErrors({});
      
      // Wait a moment before redirecting
      setTimeout(() => {
        // Redirect to admin/dashboard with leads module and open=new parameter
        navigate("/admin-dashboard?module=leads&open=new");
      }, 2000);
      
    } catch (error: any) {
      const errorMessage = typeof error?.message === 'string' ? error.message.toLowerCase() : "";
      const isRateLimited = errorMessage.includes('too many submissions');
      const isDuplicate = errorMessage.includes('duplicate key value');
      const message =
        isRateLimited || isDuplicate
          ? 'Não foi possível enviar agora. Tente novamente em alguns minutos.'
          : 'Erro ao enviar formulário. Por favor, tente novamente.';

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container max-w-7xl mx-auto px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#261E13] mb-4">
            Solicite seu orçamento
          </h2>
          <p className="text-lg text-[#261E13]/70">
            Preencha o formulário abaixo e nossa equipe entrará em contato com você em até 24 horas
          </p>
        </div>
        
        <div className="bg-white rounded-lg p-8 shadow-lg border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot field - hidden from users but visible to bots */}
            <div style={{ display: 'none' }}>
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#261E13]">Nome*</Label>
              <Input
                id="name"
                type="text"
                placeholder="Seu nome completo"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className={`rounded-md border-gray-300 ${errors.name ? 'border-red-500' : ''}`}
                maxLength={100}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#261E13]">E-mail*</Label>
              <Input
                id="email"
                type="email"
                placeholder="Seu e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`rounded-md border-gray-300 ${errors.email ? 'border-red-500' : ''}`}
                maxLength={255}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#261E13]">Telefone*</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="Seu telefone com DDD"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className={`rounded-md border-gray-300 ${errors.phone ? 'border-red-500' : ''}`}
                maxLength={15}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>
            
            <div className="w-full">
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF5605] hover:bg-[#E14D04] text-white rounded-full py-6 text-lg font-medium"
              >
                {loading ? 'Enviando...' : 'Sim, quero saber mais'}
              </Button>
            </div>
          </form>
          
          <p className="text-xs text-center mt-6 text-[#261E13]/60">
            Ao enviar este formulário, você concorda com nossa{' '}
            <a href="/privacy-policy" className="text-[#3332FC] hover:underline">
              Política de Privacidade
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TocFotosContactForm;
